<div class="admin-container">
  <div class="admin-header">
    <h1 class="admin-title">管理員控制台</h1>
    <p class="admin-subtitle">{{ eventName }} - 賬戶審核系統</p>
  </div>

  <!-- Login Form Section -->
  <div *ngIf="!isLoggedIn" class="login-section">
    <div class="login-form-container">
      <h2 class="login-title">管理員登入</h2>

      <form [formGroup]="loginForm" (ngSubmit)="onLogin()" class="login-form">
        <div class="form-group">
          <label for="username" class="form-label">用戶電郵</label>
          <input
            id="username"
            type="email"
            formControlName="username"
            [class.error]="loginForm.get('username')?.invalid && loginForm.get('username')?.touched"
            placeholder="請輸入用戶電郵"
            class="form-input"
          >
          <div *ngIf="loginForm.get('username')?.invalid && loginForm.get('username')?.touched" class="error-message">
            請輸入有效的電郵地址
          </div>
        </div>

        <div class="form-group">
          <label for="password" class="form-label">密碼</label>
          <input
            id="password"
            type="password"
            formControlName="password"
            [class.error]="loginForm.get('password')?.invalid && loginForm.get('password')?.touched"
            placeholder="請輸入密碼"
            class="form-input"
          >
          <div *ngIf="loginForm.get('password')?.invalid && loginForm.get('password')?.touched" class="error-message">
            請輸入密碼
          </div>
        </div>

        <div *ngIf="loginError" class="error-message general-error">
          {{ loginError }}
        </div>

        <button
          type="submit"
          [disabled]="loginSubmitting"
          class="login-button"
          [class.submitting]="loginSubmitting"
        >
          {{ loginSubmitting ? '登入中...' : '登入' }}
        </button>
      </form>
    </div>
  </div>

  <!-- Admin Dashboard Section -->
  <div *ngIf="isLoggedIn" class="dashboard-section">
    <!-- Header with logout -->
    <div class="dashboard-header">
      <div class="header-actions">
        <h2 class="dashboard-title"></h2>
        <button (click)="onLogout()" class="logout-button">
          登出
        </button>
      </div>

      <!-- Stats -->
      <div class="stats-container">
        <div class="stat-card">
          <div class="stat-number">{{ pendingAccounts.length }}</div>
          <div class="stat-label">待審核賬戶</div>
        </div>
        <div class="stat-card approved">
          <div class="stat-number">{{ approvedAccounts.length }}</div>
          <div class="stat-label">已批准賬戶</div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="accountsLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <p class="loading-text">載入中...</p>
    </div>

    <!-- No Data State -->
    <div *ngIf="!accountsLoading && pendingAccounts.length === 0" class="no-data-container">
      <div class="no-data-icon">📋</div>
      <h3 class="no-data-title">暫無待審核賬戶</h3>
      <p class="no-data-text">所有賬戶申請已處理完畢</p>
      <button (click)="refreshAccounts()" class="refresh-button">
        重新載入
      </button>
    </div>

    <!-- Accounts Table -->
    <div *ngIf="!accountsLoading && pendingAccounts.length > 0" class="accounts-table-container">
      <h2 class="table-title">待審核賬戶</h2>
      <div class="table-wrapper">
        <table class="accounts-table">
          <thead>
            <tr>
              <th>姓氏</th>
              <th>名字</th>
              <th>電郵地址</th>
              <th>手機號碼</th>
              <th>隸屬學校</th>
              <th>申請時間</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let account of pendingAccounts" class="account-row">
              <td>{{ account.last_name }}</td>
              <td>{{ account.first_name }}</td>
              <td>{{ account.email }}</td>
              <td>{{ account.phone || '-' }}</td>
              <td class="affiliation-cell">{{ account.affiliation }}</td>
              <td>{{ formatDate(account.created_at) }}</td>
              <td class="actions-cell">
                <button
                  (click)="showApprovalConfirmation(account)"
                  [disabled]="processingAccounts.includes(account.id)"
                  class="approve-button"
                  [class.processing]="processingAccounts.includes(account.id)"
                >
                  {{ processingAccounts.includes(account.id) ? '處理中...' : '批准' }}
                </button>
                <button
                  (click)="showRejectionConfirmation(account)"
                  [disabled]="processingAccounts.includes(account.id)"
                  class="reject-button"
                  [class.processing]="processingAccounts.includes(account.id)"
                >
                  {{ processingAccounts.includes(account.id) ? '處理中...' : '拒絕' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Approved Accounts Section -->
    <div class="approved-section">
      <h2 class="approved-title">已批准賬戶記錄</h2>

      <!-- No Approved Data State -->
      <div *ngIf="approvedAccounts.length === 0" class="no-data-container">
        <div class="no-data-icon">✅</div>
        <h3 class="no-data-title">暫無已批准賬戶</h3>
        <p class="no-data-text">尚未有任何賬戶被批准</p>
      </div>

      <!-- Approved Accounts Table -->
      <div *ngIf="approvedAccounts.length > 0" class="accounts-table-container">
        <div class="table-wrapper">
          <table class="accounts-table">
            <thead>
              <tr>
                <th>姓氏</th>
                <th>名字</th>
                <th>電郵地址</th>
                <th>手機號碼</th>
                <th>隸屬學校</th>
                <th>批准時間</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let account of approvedAccounts" class="account-row approved-row">
                <td>{{ account.last_name }}</td>
                <td>{{ account.first_name }}</td>
                <td>{{ account.email }}</td>
                <td>{{ account.phone || '-' }}</td>
                <td class="affiliation-cell">{{ account.affiliation }}</td>
                <td>{{ formatDate(account.teacher_account_approved_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>

  <!-- Approval Confirmation Dialog -->
  <div *ngIf="showApprovalDialog" class="dialog-overlay" (click)="closeApprovalDialog()">
    <div class="dialog-container" (click)="$event.stopPropagation()">
      <div class="dialog-header">
        <h3 class="dialog-title">確認批准賬戶</h3>
        <button (click)="closeApprovalDialog()" class="dialog-close-button">×</button>
      </div>

      <div class="dialog-content">
        <p class="dialog-message">您即將批准以下用戶的賬戶申請：</p>

        <div *ngIf="selectedAccount" class="user-info-card">
          <div class="user-info-row">
            <span class="info-label">姓名：</span>
            <span class="info-value">{{ selectedAccount.last_name }} {{ selectedAccount.first_name }}</span>
          </div>
          <div class="user-info-row">
            <span class="info-label">電郵：</span>
            <span class="info-value">{{ selectedAccount.email }}</span>
          </div>
          <div class="user-info-row">
            <span class="info-label">手機號碼：</span>
            <span class="info-value">{{ selectedAccount.phone || '-' }}</span>
          </div>
          <div class="user-info-row">
            <span class="info-label">隸屬學校：</span>
            <span class="info-value">{{ selectedAccount.affiliation }}</span>
          </div>
          <div class="user-info-row">
            <span class="info-label">申請時間：</span>
            <span class="info-value">{{ formatDate(selectedAccount.created_at) }}</span>
          </div>
        </div>

        <p class="dialog-warning">批准後，用戶將可以正常使用系統。此操作無法撤銷。</p>
      </div>

      <div class="dialog-actions">
        <button (click)="closeApprovalDialog()" class="dialog-cancel-button">
          取消
        </button>
        <button
          (click)="confirmApproval()"
          [disabled]="processingAccounts.includes(selectedAccount?.id || '')"
          class="dialog-confirm-button approve"
          [class.processing]="processingAccounts.includes(selectedAccount?.id || '')"
        >
          {{ processingAccounts.includes(selectedAccount?.id || '') ? '處理中...' : '確認批准' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Rejection Confirmation Dialog -->
  <div *ngIf="showRejectionDialog" class="dialog-overlay" (click)="closeRejectionDialog()">
    <div class="dialog-container" (click)="$event.stopPropagation()">
      <div class="dialog-header">
        <h3 class="dialog-title">確認拒絕賬戶</h3>
        <button (click)="closeRejectionDialog()" class="dialog-close-button">×</button>
      </div>

      <div class="dialog-content">
        <p class="dialog-message">您即將拒絕以下用戶的賬戶申請：</p>

        <div *ngIf="selectedAccount" class="user-info-card">
          <div class="user-info-row">
            <span class="info-label">姓名：</span>
            <span class="info-value">{{ selectedAccount.last_name }} {{ selectedAccount.first_name }}</span>
          </div>
          <div class="user-info-row">
            <span class="info-label">電郵：</span>
            <span class="info-value">{{ selectedAccount.email }}</span>
          </div>
          <div class="user-info-row">
            <span class="info-label">手機號碼：</span>
            <span class="info-value">{{ selectedAccount.phone || '-' }}</span>
          </div>
          <div class="user-info-row">
            <span class="info-label">隸屬學校：</span>
            <span class="info-value">{{ selectedAccount.affiliation }}</span>
          </div>
          <div class="user-info-row">
            <span class="info-label">申請時間：</span>
            <span class="info-value">{{ formatDate(selectedAccount.created_at) }}</span>
          </div>
        </div>

        <div class="rejection-reason-section">
          <label for="rejectionReason" class="reason-label">拒絕原因 (選填)：</label>
          <textarea
            id="rejectionReason"
            [(ngModel)]="rejectionReason"
            [class.error]="rejectionReasonError"
            class="reason-textarea"
            placeholder="請輸入拒絕原因（最多500字）..."
            maxlength="500"
            rows="4"
          ></textarea>
          <div class="reason-counter">{{ rejectionReason.length }}/500</div>
          <div *ngIf="rejectionReasonError" class="reason-error">{{ rejectionReasonError }}</div>
        </div>

        <p class="dialog-warning">拒絕後，用戶將收到通知郵件。此操作無法撤銷。</p>
      </div>

      <div class="dialog-actions">
        <button (click)="closeRejectionDialog()" class="dialog-cancel-button">
          取消
        </button>
        <button
          (click)="confirmRejection()"
          [disabled]="processingAccounts.includes(selectedAccount?.id || '')"
          class="dialog-confirm-button reject"
          [class.processing]="processingAccounts.includes(selectedAccount?.id || '')"
        >
          {{ processingAccounts.includes(selectedAccount?.id || '') ? '處理中...' : '確認拒絕' }}
        </button>
      </div>
    </div>
  </div>
</div>
